import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthorizationSource, Insurance } from "../types";

type InsuranceSliceState = {
    insurances: Insurance[];
    loading: boolean;
    error: string | null;
    byPassLoading: boolean;
    byPassError: string | null;
    notAutoAddLoading: boolean;
    notAutoAddError: string | null;
    noCancelFeeLoading: boolean;
    noCancelFeeError: string | null;
  }
  
  const initialState: InsuranceSliceState = {
    insurances: [],
    loading: false,
    error: null,
    byPassLoading: false,
    byPassError: null,
    notAutoAddLoading: false,
    notAutoAddError: null,
    noCancelFeeLoading: false,
    noCancelFeeError: null,
  };

export const fetchInsurances = createAsyncThunk<Insurance[], string>(
    "provider/fetchInsurances",
    async (token, { rejectWithValue }) => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/insurance`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return response.data.insurances;
      } catch (error) {
        console.log(error);
        return rejectWithValue(new Error("Failed to fetch insurances"));
      }
    }
);

export const updateByPassAuthorization = createAsyncThunk<Insurance[], {token: string, insuranceIds: string[]}>(
    "provider/updateByPassAuthorization",
    async ({token, insuranceIds}, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/insurance/byPassAuthorization`, insuranceIds, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data.insurances;
        } catch (error) {
            console.log(error);
            return rejectWithValue(new Error("Failed to update by pass authorization"));
        }
    }
);

export const updateNotAutoAddInsurances = createAsyncThunk<Insurance[], {token: string, insuranceIds: string[]}>(
    "provider/updateNotAutoAddInsurances",
    async ({token, insuranceIds}, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/insurance/notAutoAdd`, insuranceIds, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data.insurances;
        } catch (error) {
            console.log(error);
            return rejectWithValue(new Error("Failed to update not auto add insurances"));
        }
    }
);

export const updateInsuranceAuthPriority = createAsyncThunk<
  { insuranceId: string; authorizationSourcePriority: AuthorizationSource[] },
  { token: string; insuranceId: string; authorizationSourcePriority: AuthorizationSource[] }
>(
    "insurances/updateInsuranceAuthPriority",
    async ({ token, insuranceId, authorizationSourcePriority }, { rejectWithValue }) => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/insurance/${insuranceId}`,
                { authorizationSourcePriority },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return { insuranceId, authorizationSourcePriority };
        } catch (error) {
            return rejectWithValue(new Error("Failed to update authorization source priority"));
        }
    }
);

export const updateNoCancelFeeInsurances = createAsyncThunk<Insurance[], {token: string, insuranceIds: string[]}>(
    "provider/updateNoCancelFeeInsurances",
    async ({token, insuranceIds}, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/insurance/noCancelFee`, insuranceIds, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data.insurances;
        } catch (error) {
            console.log(error);
            return rejectWithValue(new Error("Failed to update no cancel fee insurances"));
        }
    }
);

const insurancesSlice = createSlice({
    name: "insurances",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchInsurances.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchInsurances.fulfilled, (state, action) => {
            state.insurances = action.payload;
            state.loading = false;
        });
        builder.addCase(fetchInsurances.rejected, (state, action) => {
            state.error = action.error.message || "Failed to fetch insurances";
            state.loading = false;
        });
        builder.addCase(updateByPassAuthorization.pending, (state) => {
            state.byPassLoading = true;
        });
        builder.addCase(updateByPassAuthorization.fulfilled, (state, action) => {
            const updatedIds = action.meta.arg.insuranceIds;
            state.insurances = state.insurances.map((insurance) => ({
                ...insurance,
                byPassAuthorization: updatedIds.includes(insurance.insuranceId),
            }));
            state.byPassLoading = false;
            state.byPassError = null;
        });
        builder.addCase(updateByPassAuthorization.rejected, (state, action) => {
            state.byPassError = action.error.message || "Failed to update by pass authorization";
            state.byPassLoading = false;
        });
        builder.addCase(updateNotAutoAddInsurances.pending, (state) => {
            state.notAutoAddLoading = true;
        });
        builder.addCase(updateNotAutoAddInsurances.fulfilled, (state, action) => {
            const updatedIds = action.meta.arg.insuranceIds;
            state.insurances = state.insurances.map((insurance) => ({
                ...insurance,
                notAutoAdd: updatedIds.includes(insurance.insuranceId),
            }));
            state.notAutoAddLoading = false;
            state.notAutoAddError = null;
        });
        builder.addCase(updateNotAutoAddInsurances.rejected, (state, action) => {
            state.notAutoAddError = action.error.message || "Failed to update not auto add insurances";
            state.notAutoAddLoading = false;
        });
        builder.addCase(updateNoCancelFeeInsurances.pending, (state) => {
            state.noCancelFeeLoading = true;
        });
        builder.addCase(updateNoCancelFeeInsurances.fulfilled, (state, action) => {
            const updatedIds = action.meta.arg.insuranceIds;
            state.insurances = state.insurances.map((insurance) => ({
                ...insurance,
                noCancelFee: updatedIds.includes(insurance.insuranceId),
            }));
            state.noCancelFeeLoading = false;
            state.noCancelFeeError = null;
        });
        builder.addCase(updateNoCancelFeeInsurances.rejected, (state, action) => {
            state.noCancelFeeError = action.error.message || "Failed to update no cancel fee insurances";
            state.noCancelFeeLoading = false;
        });
        builder.addCase(updateInsuranceAuthPriority.fulfilled, (state, action) => {
            const { insuranceId, authorizationSourcePriority } = action.payload;
            state.insurances = state.insurances.map((insurance) =>
                insurance.insuranceId === insuranceId
                    ? { ...insurance, authorizationSourcePriority }
                    : insurance
            );
        });
    }
});

export default insurancesSlice.reducer;